import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import API_BASE_URL from "../config";
import "../styles/BrandSlider.css";

const ensureHttpsIfNeeded = (u) => {
  if (!u) return u;
  if (typeof u !== 'string') return u;
  try {
    if (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:' && u.startsWith('http://')) {
      return 'https://' + u.slice('http://'.length);
    }
  } catch (_) { }
  return u;
};

export default function BrandSlider({
  brands = [],
  config = {},
  serverBase = "",
}) {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const [globalBrandLogos, setGlobalBrandLogos] = useState({});
  const [failedLogos, setFailedLogos] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/brand-logos`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data && typeof data === 'object' && !Array.isArray(data)) {
          setGlobalBrandLogos(data);
        }
      } catch (_) {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const resolveGlobalLogo = useCallback((brandName) => {
    if (!brandName) return '';
    const direct = globalBrandLogos && globalBrandLogos[brandName];
    if (direct) return direct;
    const lower = String(brandName).toLowerCase();
    if (globalBrandLogos && globalBrandLogos[lower]) return globalBrandLogos[lower];
    if (globalBrandLogos && typeof globalBrandLogos === 'object') {
      // case-insensitive match against keys
      const keys = Object.keys(globalBrandLogos);
      const foundKey = keys.find((k) => String(k).toLowerCase() === lower);
      if (foundKey) return globalBrandLogos[foundKey];
    }
    return '';
  }, [globalBrandLogos]);

  const markFailed = (key) => {
    if (!key) return;
    setFailedLogos((prev) => ({ ...(prev || {}), [key]: true }));
  };

  const sortedBrands = useMemo(() => {
    const list = Array.isArray(brands) ? [...brands] : [];
    const brandOrder = (config && Array.isArray(config.brandOrder)) ? config.brandOrder : null;
    if (!brandOrder || !brandOrder.length) return list;

    const map = new Map();
    list.forEach(b => {
      const name = (b?.name || '').trim();
      if (name) map.set(name.toLowerCase(), b);
    });

    const result = [];
    const addedKeys = new Set();

    brandOrder.forEach(orderName => {
      const lower = String(orderName || '').trim().toLowerCase();
      if (map.has(lower) && !addedKeys.has(lower)) {
        result.push(map.get(lower));
        addedKeys.add(lower);
      }
    });

    list.forEach(b => {
      const lower = String(b?.name || '').trim().toLowerCase();
      if (lower && !addedKeys.has(lower)) {
        result.push(b);
        addedKeys.add(lower);
      }
    });

    return result;
  }, [brands, config]);

  const endPageSliderRenderableBrands = useMemo(() => {
    const list = sortedBrands;
    // Only show brands that actually have a logo source (no placeholders).
    return list.filter((b) => {
      const name = (b?.name || '').trim();
      if (!name) return false;
      const custom = (config && config.customLogos && typeof config.customLogos === 'object')
        ? (config.customLogos[name] || config.customLogos[name.toLowerCase()] || '')
        : '';
      const globalLogo = resolveGlobalLogo(name);
      const raw = custom || globalLogo || '';
      if (!raw) return false;
      if (failedLogos && failedLogos[raw]) return false;
      return true;
    });
  }, [sortedBrands, config, failedLogos, resolveGlobalLogo]);

  const count = endPageSliderRenderableBrands.length;
  const itemWidth = 116; // 100px + 16px margin (8px left + 8px right)

  // Expand buffer dynamically so fast sliding never runs out of rendered items
  const repeatCount = useMemo(() => {
    if (!count) return 0;
    return Math.max(5, Math.ceil(36 / count));
  }, [count]);

  const displayBrands = useMemo(() => {
    if (count === 0) return [];
    const arr = [];
    for (let i = 0; i < repeatCount; i++) {
      arr.push(...endPageSliderRenderableBrands);
    }
    return arr;
  }, [endPageSliderRenderableBrands, count, repeatCount]);

  const [currentIndex, setCurrentIndex] = useState(() => (count > 0 ? Math.floor(Math.max(5, Math.ceil(36 / count)) / 2) * count : 0));
  const [isTransitioning, setIsTransitioning] = useState(true);

  // Sync index if brand count or repeat buffer changes
  useEffect(() => {
    if (count > 0) {
      setCurrentIndex(Math.floor(repeatCount / 2) * count);
    }
  }, [count, repeatCount]);

  const [isPaused, setIsPaused] = useState(false);
  const pauseTimerRef = useRef(null);

  const pauseAutoScrollTemporarily = () => {
    setIsPaused(true);
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    pauseTimerRef.current = setTimeout(() => {
      setIsPaused(false);
    }, 4000);
  };

  // Faster auto-scroll logic (every 950ms)
  useEffect(() => {
    if (isPaused || count <= 1) return;

    const interval = setInterval(() => {
      const middle = Math.floor(repeatCount / 2) * count;
      const maxSafe = (repeatCount - 2) * count;

      setCurrentIndex((curr) => {
        if (curr >= maxSafe) {
          setIsTransitioning(false);
          const resetIndex = middle + (curr % count);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setIsTransitioning(true);
              setCurrentIndex(resetIndex + 1);
            });
          });
          return resetIndex;
        }
        setIsTransitioning(true);
        return curr + 1;
      });
    }, 950);

    return () => clearInterval(interval);
  }, [isPaused, count, repeatCount]);

  // Seamless loop reset when reaching boundary
  const handleTransitionEnd = () => {
    if (count <= 0) return;
    const middle = Math.floor(repeatCount / 2) * count;
    if (currentIndex >= (repeatCount - 2) * count) {
      setIsTransitioning(false);
      setCurrentIndex(middle + (currentIndex % count));
    } else if (currentIndex <= count) {
      setIsTransitioning(false);
      setCurrentIndex(middle + (currentIndex % count));
    }
  };

  const endPageSliderPrev = () => {
    pauseAutoScrollTemporarily();
    const middle = Math.floor(repeatCount / 2) * count;

    if (currentIndex <= count) {
      setIsTransitioning(false);
      const resetIndex = middle + (currentIndex % count);
      setCurrentIndex(resetIndex);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsTransitioning(true);
          setCurrentIndex(resetIndex - 1);
        });
      });
    } else {
      setIsTransitioning(true);
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const endPageSliderNext = () => {
    pauseAutoScrollTemporarily();
    const middle = Math.floor(repeatCount / 2) * count;
    const maxSafe = (repeatCount - 2) * count;

    if (currentIndex >= maxSafe) {
      setIsTransitioning(false);
      const resetIndex = middle + (currentIndex % count);
      setCurrentIndex(resetIndex);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsTransitioning(true);
          setCurrentIndex(resetIndex + 1);
        });
      });
    } else {
      setIsTransitioning(true);
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const dragStartXRef = useRef(0);
  const isDraggingRef = useRef(false);
  const hasMovedRef = useRef(false);

  const onPointerDown = (e) => {
    if (count <= 1) return;
    const clientX = e.clientX ?? (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    dragStartXRef.current = clientX;
    isDraggingRef.current = true;
    hasMovedRef.current = false;
  };

  const onPointerMove = (e) => {
    if (!isDraggingRef.current) return;
    const clientX = e.clientX ?? (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    if (Math.abs(clientX - dragStartXRef.current) > 8) {
      hasMovedRef.current = true;
    }
  };

  const onPointerUp = (e) => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    const endX = e.clientX ?? (e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : dragStartXRef.current);
    const diffX = endX - dragStartXRef.current;
    if (Math.abs(diffX) >= 35) {
      if (diffX < 0) {
        endPageSliderNext();
      } else {
        endPageSliderPrev();
      }
    }
  };

  if (count === 0) {
    return (
      <section className="w-full mt-6 text-center text-gray-400 py-10">
        No brands available
      </section>
    );
  }

  return (
    <section className="w-full mt-10 sm:mt-14 mb-[36px] pb-4" style={{ marginTop: 48, marginBottom: 36, paddingBottom: 16 }}>
      <div className="relative w-[82%] mx-auto">
        {config?.title && (
          <h2
            className="text-2xl sm:text-2xl md:text-3xl font-semibold"
            style={{ margin: '0 0 20px 0', color: config?.titleColor || '#094859' }}
          >
            {config.title}
          </h2>
        )}
        {/* VIEWPORT */}
        <div
          className="overflow-hidden"
          ref={viewportRef}
          onMouseEnter={() => setIsPaused(true)}
          onMouseDown={onPointerDown}
          onMouseMove={onPointerMove}
          onMouseUp={onPointerUp}
          onMouseLeave={() => { setIsPaused(false); onPointerUp(); }}
          onTouchStart={onPointerDown}
          onTouchMove={onPointerMove}
          onTouchEnd={onPointerUp}
          onTouchCancel={onPointerUp}
          style={{ cursor: count > 1 ? 'grab' : 'default', touchAction: 'pan-y', userSelect: 'none' }}
        >
          {/* SLIDER TRACK */}
          <div
            ref={trackRef}
            className="flex"
            onTransitionEnd={handleTransitionEnd}
            onClickCapture={(e) => {
              if (hasMovedRef.current) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            style={{
              width: "max-content",
              transform: `translateX(-${currentIndex * itemWidth}px)`,
              transition: isTransitioning ? "transform 280ms cubic-bezier(0.25, 1, 0.5, 1)" : "none",
            }}
          >
            {displayBrands.map((b, idx) => {
              const name = (b?.name || '').trim();

              const custom = (config && config.customLogos && typeof config.customLogos === 'object')
                ? (config.customLogos[name] || config.customLogos[name.toLowerCase()] || '')
                : '';

              const globalLogo = resolveGlobalLogo(name);
              const raw = custom || globalLogo || '';
              if (!raw) return null;
              const logoPath = raw.startsWith("http")
                ? ensureHttpsIfNeeded(raw)
                : `${serverBase}${raw.startsWith("/") ? raw : "/" + raw}`;

              return (
                <div
                  key={`${b?.name || 'brand'}-${idx}`}
                  className="w-[100px] flex-shrink-0 border border-gray-200 bg-white mx-2 brand-card-container"
                >
                  <Link
                    to={`/products?brand=${encodeURIComponent(
                      b?.name || ""
                    )}`}
                  >
                    <div className="w-[100px] h-[80px] brand-image-container">
                      <img
                        src={logoPath}
                        alt={b?.name || "Brand"}
                        className="max-w-[100%] max-h-[100%] text-center object-contain brand-logo-img"
                        onError={() => markFailed(raw)}
                      />
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>

        <div className="absolute bottom-[-3rem] right-0 flex gap-2">
          {/* Previous */}
          <button
            type="button"
            onClick={endPageSliderPrev}
            aria-label="Previous products"
            className="glb-slider-nav-btn"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          </button>

          {/* Next */}
          <button
            type="button"
            onClick={endPageSliderNext}
            aria-label="Next products"
            className="glb-slider-nav-btn"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
          </button>
        </div>
      </div>
    </section>
  );
}
